// BUILD-046 display-only cleanup, cycle D1: how people, jobs, groups and task titles are SHOWN in Shared Tasks / My Work.
//
// DISPLAY ONLY. Nothing here is ever stored, sent or compared: stored names, titles, ids, assignments and permissions are not touched, and search and
// sorting keep reading the STORED values (a search for "alice acc" still finds "Alice Accounting (FICTIONAL)"). Every function returns NEW strings and
// never changes its input. Inputs of an edit form keep their stored text (an Edit title input keeps a "(FICTIONAL)" suffix); only labels change.
//
// 1. stripTestMarker(text): removes every parenthesized "(FICTIONAL)" (any case, optional spaces inside the parentheses) with its surrounding spaces,
//    collapses whitespace and trims. A non-string gives ''. "(you)" is NOT a marker and is kept. A marker followed at once by punctuation leaves no space behind
//    it ("Accounting (FICTIONAL), Alice" -> "Accounting, Alice"). The single banner "Simulated preview · Test data" says once that everything on the page is
//    test data, so the suffix is not repeated on every label.
//
// 2. personDisplay(person, extra?): { full, short, first, hasSurname }. The API shapes carry NO structured name fields: WorkEmployeeRef is
//    { employeeId, displayName, department? }, a task carries ownerName / requesterName / completedByName / claim.byName as plain strings, recipients are
//    { employeeId, name }, and the pilot personas are { key, name } with a department held in the directory. So the working path is the display STRING.
//    The structured keys below are read when a source ever supplies them (givenName / familyName, firstName / lastName, given_name / family_name,
//    first_name / last_name) and win over parsing:
//      structured:  "First L."  (the initial is the first letter of the FIRST WORD of the family name, upper case; a hyphenated or multi-word given name
//                   is kept whole: "Mary-Anne O."); only a given name -> the given name.
//      display string:  marker and caller tag removed; a trailing generational suffix (Jr, Sr, II, III, IV) dropped; one word -> that word; otherwise the
//                   LAST word is the surname and the short form is "FirstWord L." UNLESS the last word is a ROLE WORD, which is never a surname: then the
//                   role word(s) are dropped and what is left is the given name ("Chris Owner" -> "Chris"). Never a surname invented, never a role as one.
//    A role word is: (a) the person's OWN department or role (any of department, role, jobTitle, position, roles[], groups[] the data supplies, compared
//    as whole text, case-insensitive), or (b) one of ROLE_WORDS, derived from the pilot persona fixture (local-pilot/shared-tasks/lib/personas.mjs,
//    read only): the trailing role word of each persona name (Owner, Manager, Employee, Peer, Designer, Accounting, Admin, Deactivated), the fixture
//    departments (Leadership, Operations, Design, Accounting, IT - "Field" is left out on purpose: it is an ordinary surname, so it only counts when it is
//    the person's own department), the fixture group keys (owner, pm, superintendent, designer, accounting, admin) and the generic words Owner, Manager,
//    Employee, Admin, Administrator.
//    "(you)" and "(me)" at the end of a name are caller tags, not part of the name: callers append them AFTER the short form ("Alice (you)").
//    The placeholders the app writes where it does not know a person ("Unknown person", "Unknown employee", "someone") are shown as they are, never shortened.
//
// 3. disambiguatePeople(list): for search results and pick lists. Each person gets the quiet second line (the department, or the role when there is no
//    department). When two people still share the same short name AND department, the full name is added; when they still cannot be told apart (same full
//    name too), an identifying suffix from the data is added (the e-mail local part, else the last 4 characters of the id). The list is judged as a whole
//    (the whole roster, not just the rows a search leaves), so a label does not change as you type.
//
// Pure module: no React, no DOM, no I/O. Erasable TypeScript only.

// ───────────────────────── the test marker ─────────────────────────

const MARKER = /\s*\(\s*fictional\s*\)\s*/gi;
// A marker that is followed at once by punctuation leaves no space behind it ("Accounting (FICTIONAL), Alice" -> "Accounting, Alice"; "Quote (FICTIONAL).pdf" -> "Quote.pdf").
const MARKER_BEFORE_PUNCTUATION = /\s*\(\s*fictional\s*\)(?=[.,;:!?)\]])/gi;

/**
 * The text without any parenthesized "(FICTIONAL)", with the spaces around it, whitespace collapsed and trimmed. A non-string gives ''.
 * "Alice Accounting (FICTIONAL) (you)" -> "Alice Accounting (you)"; "Accounting (FICTIONAL)" -> "Accounting".
 */
export function stripTestMarker(text: unknown): string {
  if (typeof text !== 'string') return '';
  return text.replace(MARKER_BEFORE_PUNCTUATION, '').replace(MARKER, ' ').replace(/\s+/g, ' ').trim();
}

// ───────────────────────── role words ─────────────────────────

const PERSONA_ROLE_WORDS = ['Owner', 'Manager', 'Employee', 'Peer', 'Designer', 'Accounting', 'Admin', 'Deactivated'];
const FIXTURE_DEPARTMENT_WORDS = ['Leadership', 'Operations', 'Design', 'Accounting', 'IT'];
const FIXTURE_GROUP_WORDS = ['owner', 'pm', 'superintendent', 'designer', 'accounting', 'admin'];
const GENERIC_ROLE_WORDS = ['Owner', 'Manager', 'Employee', 'Admin', 'Administrator'];

/** The words that are never a surname (lower case). See the header for where each part comes from. */
export const ROLE_WORDS: ReadonlySet<string> = new Set([...PERSONA_ROLE_WORDS, ...FIXTURE_DEPARTMENT_WORDS, ...FIXTURE_GROUP_WORDS, ...GENERIC_ROLE_WORDS].map((w) => w.toLowerCase()));

/** A word compared as text: lower case, without punctuation at either end. */
function wordKey(word: string): string {
  return word.toLowerCase().replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
}

// ───────────────────────── a person ─────────────────────────

/** Fields a person source MAY carry. Every one is optional and read defensively (any value type). */
export interface PersonFields {
  displayName?: unknown;
  name?: unknown;
  fullName?: unknown;
  full_name?: unknown;
  display_name?: unknown;
  givenName?: unknown;
  familyName?: unknown;
  firstName?: unknown;
  lastName?: unknown;
  given_name?: unknown;
  family_name?: unknown;
  first_name?: unknown;
  last_name?: unknown;
  department?: unknown;
  role?: unknown;
  roles?: unknown;
  groups?: unknown;
  jobTitle?: unknown;
  position?: unknown;
}

/** A display string or an object that may carry structured fields. */
export type PersonInput = string | PersonFields | null | undefined;

/** Extra facts about the same person (when the name is a plain string, the department / role come from the directory beside it). */
export interface PersonExtra {
  department?: unknown;
  role?: unknown;
  roles?: unknown;
}

export interface PersonDisplay {
  /** The whole name, marker and caller tag removed ("Alice Accounting"). */
  full: string;
  /** What chips and rows show ("Alice A." / "Alice"). */
  short: string;
  /** The first name alone ("Alice"). */
  first: string;
  /** True when `short` carries a surname initial. */
  hasSurname: boolean;
}

const NAME_KEYS = ['displayName', 'name', 'fullName', 'full_name', 'display_name'] as const;
const GIVEN_KEYS = ['givenName', 'firstName', 'given_name', 'first_name'] as const;
const FAMILY_KEYS = ['familyName', 'lastName', 'family_name', 'last_name'] as const;
const ROLE_KEYS = ['department', 'role', 'jobTitle', 'position'] as const;
const CALLER_TAG = /\s*\(\s*(?:you|me)\s*\)\s*$/i;
const GENERATION = /^(?:jr|sr|ii|iii|iv)\.?$/i;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

function firstText(source: Record<string, unknown>, keys: readonly string[]): string {
  for (const key of keys) {
    const text = stripTestMarker(source[key]);
    if (text !== '') return text;
  }
  return '';
}

/** The upper-case first LETTER of a word (code-point safe), or ''. */
function initialOf(word: string): string {
  const match = /\p{L}/u.exec(word);
  if (match === null) return '';
  return Array.from(match[0].toLocaleUpperCase())[0] ?? '';
}

function addRoleText(into: Set<string>, value: unknown): void {
  if (Array.isArray(value)) {
    for (const item of value) addRoleText(into, item);
    return;
  }
  const key = wordKey(stripTestMarker(value));
  if (key !== '') into.add(key);
}

function ownRoles(source: Record<string, unknown> | null, extra: PersonExtra): Set<string> {
  const own = new Set<string>();
  if (source !== null) {
    for (const key of ROLE_KEYS) addRoleText(own, source[key]);
    addRoleText(own, source.roles);
    addRoleText(own, source.groups);
  }
  addRoleText(own, extra.department);
  addRoleText(own, extra.role);
  addRoleText(own, extra.roles);
  return own;
}

function emptyDisplay(): PersonDisplay {
  return { full: '', short: '', first: '', hasSurname: false };
}

/** The words the app writes where it does not know a person ("Unknown person", "Unknown employee"). They are not names: they are shown as they are. */
const PLACEHOLDER_NAMES: ReadonlySet<string> = new Set(['unknown', 'unknown person', 'unknown employee', 'someone', 'the recipient', 'this recipient']);

function fromString(raw: string, own: ReadonlySet<string>): PersonDisplay {
  const cleaned = stripTestMarker(raw).replace(CALLER_TAG, '').replace(/,\s*((?:jr|sr|ii|iii|iv)\.?)$/i, ' $1').trim();
  const words = cleaned.split(' ').filter((w) => w !== '');
  if (words.length === 0) return emptyDisplay();
  const full = words.join(' ');
  if (PLACEHOLDER_NAMES.has(full.toLowerCase())) return { full, short: full, first: words[0], hasSurname: false };
  const core = [...words];
  if (core.length > 1 && GENERATION.test(core[core.length - 1].replace(/,$/, ''))) core.pop();
  const first = core[0];
  if (core.length === 1) return { full, short: first, first, hasSurname: false };
  // Trailing role words are dropped (never a surname); what is left is the given name.
  let end = core.length;
  const isRole = (word: string) => ROLE_WORDS.has(wordKey(word)) || own.has(wordKey(word));
  while (end > 1 && isRole(core[end - 1])) end -= 1;
  if (end < core.length) return { full, short: core.slice(0, end).join(' '), first, hasSurname: false };
  const initial = initialOf(core[core.length - 1]);
  if (initial === '') return { full, short: first, first, hasSurname: false };
  return { full, short: `${first} ${initial}.`, first, hasSurname: true };
}

/**
 * How a person is shown. See the header for the rules. `extra` carries the department / role when `person` is only a display string. A new object is
 * returned every time; the input is never changed.
 */
export function personDisplay(person: PersonInput, extra: PersonExtra = {}): PersonDisplay {
  if (typeof person === 'string') return fromString(person, ownRoles(null, extra));
  if (!isRecord(person)) return emptyDisplay();
  const source = person as Record<string, unknown>;
  const own = ownRoles(source, extra);
  const given = firstText(source, GIVEN_KEYS);
  const family = firstText(source, FAMILY_KEYS);
  const named = firstText(source, NAME_KEYS);
  if (given !== '') {
    const surnameInitial = family === '' ? '' : initialOf(family.split(' ')[0]);
    const full = named !== '' ? fromString(named, own).full : family === '' ? given : `${given} ${family}`;
    return { full, short: surnameInitial === '' ? given : `${given} ${surnameInitial}.`, first: given, hasSurname: surnameInitial !== '' };
  }
  if (named !== '') return fromString(named, own);
  if (family !== '') return { full: family, short: family, first: family, hasSurname: false };
  return emptyDisplay();
}

/** The short form of a person ("Alice A.", "Chris"). */
export function shortName(person: PersonInput, extra: PersonExtra = {}): string {
  return personDisplay(person, extra).short;
}

/** The full name of a person, marker and caller tag removed. */
export function fullName(person: PersonInput, extra: PersonExtra = {}): string {
  return personDisplay(person, extra).full;
}

/** The short form followed by the caller tag when it is the caller: "Alice (you)". */
export function shortNameWithTag(person: PersonInput, isCaller: boolean, extra: PersonExtra = {}): string {
  const short = personDisplay(person, extra).short;
  return isCaller ? `${short} (you)` : short;
}

/** The full name when it adds something to the short form (so a hover or accessible name is worth giving), else null. */
export function fullNameIfDifferent(person: PersonInput, extra: PersonExtra = {}): string | null {
  const display = personDisplay(person, extra);
  return display.full !== '' && display.full !== display.short ? display.full : null;
}

// ───────────────────────── a task's description ─────────────────────────

/**
 * How a task's DESCRIPTION is shown (D2a browser finding). The server writes "Handed off from <full stored name>" as the description of a handed-off task that had none
 * (lib/lambda/work/task-service.ts: `notes ?? item.description ?? "Handed off from <caller name>"`). The row already says "Handed off from <short name>" on its quiet
 * line, with the full name on hover and focus, so that default text is not repeated (it would show the full name and the test marker). Any other description is the author's
 * own text and is shown as written, except that a parenthesized "(FICTIONAL)" test marker is not shown. Display only: the stored description is never changed. '' = draw nothing.
 */
export function descriptionForDisplay(description: unknown, handoffFromName?: unknown): string {
  const text = stripTestMarker(description);
  if (text === '') return '';
  const from = stripTestMarker(handoffFromName);
  return from !== '' && text === `Handed off from ${from}` ? '' : text;
}

// ───────────────────────── telling people apart in a pick list ─────────────────────────

/** A person in a search / pick list. Every field is optional and read defensively. */
export interface ChoosablePerson extends PersonFields {
  employeeId?: unknown;
  id?: unknown;
  email?: unknown;
}

export interface PersonChoice {
  /** The employee id (or '' when the source has none). */
  id: string;
  /** The short name (primary line). */
  short: string;
  /** The full name, marker removed. */
  full: string;
  /** The department, or the role when there is no department; '' when the data supplies neither. */
  department: string;
  /** The quiet second line: [full name when it was needed] · [department] · [identifying suffix when it was needed]; '' when empty. */
  detail: string;
  /** The accessible name of a pick-list option: the full name, the department and the suffix when one was needed. */
  accessibleName: string;
  /** True when the full name was added to the second line because short name and department were not enough. */
  showsFullName: boolean;
  /** The identifying suffix ('' when not needed). */
  suffix: string;
}

function localPartOf(email: unknown): string {
  if (typeof email !== 'string') return '';
  const at = email.indexOf('@');
  const local = (at < 0 ? email : email.slice(0, at)).trim();
  return local;
}

function suffixFor(person: ChoosablePerson, id: string): string {
  const local = localPartOf(person.email);
  if (local !== '') return local;
  const text = id.trim();
  return text === '' ? '' : `ID …${text.slice(-4)}`;
}

function counts(keys: readonly string[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const key of keys) map.set(key, (map.get(key) ?? 0) + 1);
  return map;
}

/**
 * The labels of a whole list of people, aligned with the input by position. Judge the WHOLE roster (not only the rows a search leaves) so a label does not
 * change as the person types. Order of escalation: the department is always shown when there is one; people who still share the short name AND the
 * department get the full name; people who still share the full name get an identifying suffix.
 */
export function disambiguatePeople(list: readonly ChoosablePerson[]): PersonChoice[] {
  const rows = (Array.isArray(list) ? list : []).map((person) => {
    const source: ChoosablePerson = isRecord(person) ? person : {};
    const department = stripTestMarker(source.department) !== '' ? stripTestMarker(source.department) : firstText(source as Record<string, unknown>, ['role', 'jobTitle', 'position']);
    const display = personDisplay(source, { department });
    const id = typeof source.employeeId === 'string' ? source.employeeId : typeof source.id === 'string' ? source.id : '';
    return { source, display, department, id };
  });
  const byShort = counts(rows.map((r) => r.display.short.toLowerCase()));
  const byShortDept = counts(rows.map((r) => `${r.display.short.toLowerCase()}\u0001${r.department.toLowerCase()}`));
  const needsFull = rows.map((r) => (byShort.get(r.display.short.toLowerCase()) ?? 0) > 1 && (byShortDept.get(`${r.display.short.toLowerCase()}\u0001${r.department.toLowerCase()}`) ?? 0) > 1 && r.display.full !== r.display.short);
  const identity = rows.map((r, i) => `${r.display.short.toLowerCase()}\u0001${r.department.toLowerCase()}\u0001${needsFull[i] ? r.display.full.toLowerCase() : ''}`);
  const byIdentity = counts(identity);
  return rows.map((r, i) => {
    const stillSame = (byShort.get(r.display.short.toLowerCase()) ?? 0) > 1 && (byIdentity.get(identity[i]) ?? 0) > 1;
    const suffix = stillSame ? suffixFor(r.source, r.id) : '';
    const detail = [needsFull[i] ? r.display.full : '', r.department, suffix].filter((part) => part !== '').join(' \u00B7 ');
    const accessibleName = [r.display.full, r.department, suffix].filter((part) => part !== '').join(', ');
    return { id: r.id, short: r.display.short, full: r.display.full, department: r.department, detail, accessibleName, showsFullName: needsFull[i], suffix };
  });
}

/** The same labels keyed by employee id (people without an id are left out). */
export function disambiguationById(list: readonly ChoosablePerson[]): Map<string, PersonChoice> {
  const map = new Map<string, PersonChoice>();
  for (const choice of disambiguatePeople(list)) if (choice.id !== '') map.set(choice.id, choice);
  return map;
}
